import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tag, Heading, Lead, PageHero, Section, BtnPrimary } from '../components/UI';
import Reveal from '../components/Reveal';
import styles from './Training.module.css';

const COURSES = [
  {
    n: '01', t: 'Java Full Stack', slug: 'java-full-stack',
    desc: 'Master enterprise-grade application development with Core Java, Spring Boot, Hibernate, REST APIs, and React frontend integration.',
    topics: ['Core Java & OOPs', 'Spring Boot & MVC', 'Hibernate & JPA', 'React.js Frontend', 'REST APIs', 'MySQL & MongoDB'],
    duration: '4 Months', mode: 'Online', pdf: '/course-pdfs/java-full-stack.pdf',
  },
  {
    n: '02', t: '.Net Full Stack', slug: 'dotnet',
    desc: 'Build powerful applications with C#, ASP.NET Core, Entity Framework, SQL Server, and Angular or React for the frontend.',
    topics: ['C# & .NET Core', 'ASP.NET MVC', 'Entity Framework', 'Angular / React', 'SQL Server', 'Azure Basics'],
    duration: '4 Months', mode: 'Online', pdf: '/course-pdfs/dotnet.pdf',
  },
  {
    n: '03', t: 'Python Full Stack', slug: 'python',
    desc: 'From Django backend to React frontend — develop full-stack web applications with Python, with hands-on project work throughout.',
    topics: ['Python Fundamentals', 'Django & Flask', 'REST APIs', 'React Frontend', 'PostgreSQL', 'Deployment & DevOps'],
    duration: '3.5 Months', mode: 'Online', pdf: '/course-pdfs/python.pdf',
  },
  {
    n: '04', t: 'AI & Machine Learning', slug: 'ai-ml',
    desc: 'Dive into machine learning, deep learning, NLP, computer vision, and AI application development with Python and TensorFlow.',
    topics: ['Python for AI', 'ML Algorithms', 'Deep Learning', 'NLP & Computer Vision', 'TensorFlow & PyTorch', 'Model Deployment'],
    duration: '4 Months', mode: 'Online', pdf: '/course-pdfs/ai-ml.pdf',
  },
  {
    n: '05', t: 'Salesforce', slug: 'salesforce',
    desc: 'Comprehensive Salesforce training covering Admin, Developer, and Architect tracks — prepare for globally recognized certifications.',
    topics: ['Salesforce Admin', 'Apex & Visualforce', 'Lightning Web Components', 'Salesforce DX', 'Integration', 'Certification Prep'],
    duration: '3 Months', mode: 'Online', pdf: '/course-pdfs/salesforce.pdf',
  },
  {
    n: '06', t: 'Power BI', slug: 'power-bi',
    desc: 'Transform raw data into powerful visual insights with Power BI Desktop, DAX, Power Query, and enterprise reporting skills.',
    topics: ['Power BI Desktop', 'DAX Formulas', 'Power Query', 'Data Modeling', 'Dashboard Design', 'Power BI Service'],
    duration: '2 Months', mode: 'Online', pdf: '/course-pdfs/power-bi.pdf',
  },
  {
    n: '07', t: 'QA Automation', slug: 'qa-automation',
    desc: 'Master modern testing frameworks including Selenium, Cypress, and TestNG, with CI/CD integration and API testing skills.',
    topics: ['Manual Testing', 'Selenium WebDriver', 'TestNG & JUnit', 'Cypress.io', 'API Testing', 'CI/CD & Jenkins'],
    duration: '3 Months', mode: 'Online', pdf: '/course-pdfs/qa-automation.pdf',
  },
  {
    n: '08', t: 'DevOps', slug: 'devops',
    desc: 'Streamline software delivery with CI/CD pipelines, containerization, infrastructure as code, and cloud-native operations.',
    topics: ['Linux & Shell Scripting', 'Git & CI/CD (Jenkins)', 'Docker & Kubernetes', 'Terraform / IaC', 'AWS / Azure DevOps', 'Monitoring & Logging'],
    duration: '3 Months', mode: 'Online',
  },
  {
    n: '09', t: 'Data Engineering', slug: 'data-engineering',
    desc: 'Design, build, and manage scalable data pipelines and architectures using modern big data and cloud tools.',
    topics: ['SQL & Python for Data', 'ETL Pipelines', 'Apache Spark', 'Data Warehousing', 'Airflow', 'Cloud Data Platforms'],
    duration: '3.5 Months', mode: 'Online',
  },
  {
    n: '10', t: 'Business Analyst', slug: 'business-analyst',
    desc: 'Bridge business and technology with requirement gathering, process modeling, and stakeholder communication skills.',
    topics: ['Requirement Elicitation', 'BPMN & Process Modeling', 'Agile & Scrum', 'SQL Basics', 'Documentation (BRD/FRD)', 'Stakeholder Communication'],
    duration: '2.5 Months', mode: 'Online',
  },
];

const FEATURES = [
  { icon: '🎓', t: 'Certified Instructors',    d: 'All courses taught by certified and experienced industry professionals.' },
  { icon: '💻', t: 'Learn from Anywhere',      d: 'Fully online — everything done from your computer\'s desktop, on your schedule.' },
  { icon: '📚', t: 'Rich Course Content',       d: 'Exhaustive explanations, detailed coverage, and practical project-based learning.' },
  { icon: '💰', t: 'Competitive Pricing',       d: 'High-quality training at affordable rates with flexible payment options.' },
  { icon: '🏆', t: 'Industry Certification',   d: 'Prepare for globally recognized certifications that boost your career.' },
  { icon: '🤝', t: 'Career Support',            d: 'Job placement assistance and interview preparation for course graduates.' },
];

export default function Training() {
  const [searchParams] = useSearchParams();
  const requestedSlug = searchParams.get('course');
  const initialIndex = COURSES.findIndex(c => c.slug === requestedSlug);
  const [selected, setSelected] = useState(initialIndex >= 0 ? initialIndex : 0);
  const course = COURSES[selected];

  return (
    <>
      <PageHero
        tag="IT Training"
        title="Learn from Certified Industry Experts"
        subtitle="Metawave Solutions offers high-quality, state-of-the-art IT and business-related e-learning training courses — competitive prices, rich content, and learner-friendly delivery."
      />

      {/* ── Features ───────────────────────────────────────────── */}
      <Section id="why-train" variant="paper">
        <Tag>Why Train With Us</Tag>
        <Heading>What Makes Our Training Different</Heading>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) =>
            /* This one is also an anchor-scroll target (see App.js's anchor-highlight
               pulse) — kept as a plain div so React re-renders from Reveal's own
               visibility state don't clobber the imperatively-added pulse class. */
            f.t === 'Career Support' ? (
              <div key={i} id="career-support" className={styles.featureCard}>
                <div className={styles.featureIcon}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.t}</h3>
                <p className={styles.featureDesc}>{f.d}</p>
              </div>
            ) : (
              <Reveal key={i} delay={(i % 3) * 0.1} className={styles.featureCard}>
                <div className={styles.featureIcon}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.t}</h3>
                <p className={styles.featureDesc}>{f.d}</p>
              </Reveal>
            )
          )}
        </div>
      </Section>

      {/* ── Course Explorer ─────────────────────────────────────── */}
      <Section id="courses" variant="white">
        <Tag>Our Curriculum</Tag>
        <Heading>Courses Offered</Heading>
        <Lead>Ten industry-aligned programs designed to launch or advance your career in tech.</Lead>
        <div className={styles.explorer}>
          {/* Sidebar */}
          <div className={styles.courseList}>
            {COURSES.map((c, i) => (
              <button
                key={i}
                className={`${styles.courseBtn} ${selected === i ? styles.courseBtnActive : ''}`}
                onClick={() => setSelected(i)}
              >
                <span className={styles.courseBtnNum}>{c.n}</span>
                <span className={styles.courseBtnTitle}>{c.t}</span>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className={styles.courseDetail} key={selected}>
            <div className={styles.detailMeta}>
              <span className={styles.metaBadge}>⏱ {course.duration}</span>
              <span className={styles.metaBadge}>🖥 {course.mode}</span>
            </div>
            <h2 className={styles.detailTitle}>{course.t}</h2>
            <p className={styles.detailDesc}>{course.desc}</p>

            <div className={styles.topicsSection}>
              <h4 className={styles.topicsHeading}>Topics Covered</h4>
              <div className={styles.topicsGrid}>
                {course.topics.map((topic, i) => (
                  <div key={i} className={styles.topicChip}>
                    <span className={styles.topicDot} />
                    {topic}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <BtnPrimary to="/contact-us">
                Enroll in {course.t} →
              </BtnPrimary>
              {course.pdf && (
                <BtnPrimary href={course.pdf}>
                  📄 Download Course Content
                </BtnPrimary>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      {/* <Section variant="ink" style={{ textAlign: 'center' }}>
        <Tag light>Get Started Today</Tag>
        <Heading light>Ready to Advance Your Career?</Heading>
        <Lead light style={{ margin: '0 auto 2rem' }}>
          Join thousands of professionals who have upskilled with Metawave Solutions training programs.
        </Lead>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <BtnPrimary to="/contact">Enroll Now</BtnPrimary>
        </div>
      </Section> */}
    </>
  );
}
